import React, { useState, useEffect } from "react";
import { Modal, Table, InputNumber, Switch, Button, message, Spin, Tooltip, Space, Alert } from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";
import { API_BASE } from "../../../config";

// Tooltip içerikleri
const tooltips = {
    quantityControl: (
        <div style={{ maxWidth: 280 }}>
            <strong>Miktar Kontrolü</strong>
            <p style={{ margin: "8px 0 4px 0" }}>
                Aynı barkod/karekod tekrar okutulduğunda ne olacağını belirler.
            </p>
            <ul style={{ paddingLeft: 16, margin: 0 }}>
                <li><b>Açık:</b> Tekrar okutulduğunda uyarı verir veya onay ister</li>
                <li><b>Kapalı:</b> Serbest giriş - kontrol yapmadan ekler</li>
            </ul>
        </div>
    ),
    duplicateControl: (
        <div style={{ maxWidth: 280 }}>
            <strong>Tekrar Kontrolü</strong>
            <p style={{ margin: "8px 0 4px 0" }}>
                Miktar kontrolü açıkken, aynı barkod/karekodun tekrar eklenmesine izin verilip verilmeyeceğini belirler.
            </p>
            <ul style={{ paddingLeft: 16, margin: 0 }}>
                <li><b>Açık:</b> Tekrar ekleme <u>engellenir</u></li>
                <li><b>Kapalı:</b> Onay ile tekrar eklenebilir</li>
            </ul>
        </div>
    ),
    combinationInfo: (
        <div style={{ maxWidth: 320 }}>
            <strong>Kontrol Kombinasyonları</strong>
            <table style={{ width: "100%", marginTop: 8, fontSize: 12 }}>
                <thead>
                    <tr style={{ borderBottom: "1px solid #444" }}>
                        <th style={{ textAlign: "left", padding: 4 }}>Miktar</th>
                        <th style={{ textAlign: "left", padding: 4 }}>Tekrar</th>
                        <th style={{ textAlign: "left", padding: 4 }}>Sonuç</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td style={{ padding: 4 }}>Kapalı</td>
                        <td style={{ padding: 4 }}>-</td>
                        <td style={{ padding: 4, color: "#52c41a" }}>Serbest giriş</td>
                    </tr>
                    <tr>
                        <td style={{ padding: 4 }}>Açık</td>
                        <td style={{ padding: 4 }}>Kapalı</td>
                        <td style={{ padding: 4, color: "#faad14" }}>Onay ile ekle</td>
                    </tr>
                    <tr>
                        <td style={{ padding: 4 }}>Açık</td>
                        <td style={{ padding: 4 }}>Açık</td>
                        <td style={{ padding: 4, color: "#ff4d4f" }}>Engelle</td>
                    </tr>
                </tbody>
            </table>
        </div>
    ),
    vatRate: "Bu ana gruba ait ürünlerin varsayılan KDV oranı",
    isActive: "Bu ana grubun sistemde aktif olup olmadığını belirler",
};

// Başlık bileşeni (tooltip ile)
const TitleWithTooltip = ({ title, tooltip }) => (
    <Space size={4}>
        {title}
        <Tooltip title={tooltip} placement="top" overlayStyle={{ maxWidth: 350 }}>
            <QuestionCircleOutlined style={{ color: "#1890ff", cursor: "help" }} />
        </Tooltip>
    </Space>
);

const ItemTypeSettingsModal = ({ open, onClose }) => {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [data, setData] = useState([]);
    const [messageApi, contextHolder] = message.useMessage();

    const token = localStorage.getItem("token");
    const dbName = localStorage.getItem("dbName");
    const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        DbName: dbName,
    };

    // Verileri yükle
    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_BASE}/ItemTypeSettings`, { headers });
            if (response.ok) {
                const result = await response.json();
                setData(result);
            } else {
                // Veri yoksa seed et
                await fetch(`${API_BASE}/ItemTypeSettings/seed`, {
                    method: "POST",
                    headers,
                });
                // Tekrar çek
                const retryResponse = await fetch(`${API_BASE}/ItemTypeSettings`, { headers });
                if (retryResponse.ok) {
                    const result = await retryResponse.json();
                    setData(result);
                }
            }
        } catch (error) {
            messageApi.error("Veriler yüklenirken hata oluştu: " + error.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) {
            fetchData();
        }
    }, [open]);

    // Satır değişikliği
    const handleChange = (id, field, value) => {
        setData((prev) =>
            prev.map((item) =>
                item.id === id ? { ...item, [field]: value } : item
            )
        );
    };

    // Kaydet
    const handleSave = async () => {
        setSaving(true);
        try {
            const response = await fetch(`${API_BASE}/ItemTypeSettings/bulk`, {
                method: "PUT",
                headers,
                body: JSON.stringify(data),
            });

            if (response.ok) {
                messageApi.success("Ayarlar başarıyla kaydedildi.");
                onClose();
                // Sayfa yenile - değişikliklerin uygulanması için
                window.location.reload();
            } else {
                const error = await response.json();
                messageApi.error(error.message || "Kaydetme başarısız.");
            }
        } catch (error) {
            messageApi.error("Kaydetme hatası: " + error.message);
        } finally {
            setSaving(false);
        }
    };

    const columns = [
        {
            title: "Ana Grup",
            dataIndex: "name",
            key: "name",
            width: 140,
            render: (text) => <strong>{text}</strong>,
        },
        {
            title: <TitleWithTooltip title="KDV Oranı" tooltip={tooltips.vatRate} />,
            dataIndex: "vatRate",
            key: "vatRate",
            width: 110,
            render: (value, record) => (
                <InputNumber
                    min={0}
                    max={100}
                    value={value}
                    onChange={(val) => handleChange(record.id, "vatRate", val)}
                    style={{ width: "100%" }}
                    addonAfter="%"
                />
            ),
        },
        {
            title: <TitleWithTooltip title="Miktar Kontrolü" tooltip={tooltips.quantityControl} />,
            dataIndex: "quantityControl",
            key: "quantityControl",
            width: 140,
            align: "center",
            render: (value, record) => (
                <Switch
                    checked={value}
                    onChange={(val) => handleChange(record.id, "quantityControl", val)}
                    checkedChildren="Açık"
                    unCheckedChildren="Kapalı"
                    style={{ backgroundColor: value ? "#389e0d" : "#cf1322" }}
                />
            ),
        },
        {
            title: <TitleWithTooltip title="Tekrar Kontrolü" tooltip={tooltips.duplicateControl} />,
            dataIndex: "duplicateControl",
            key: "duplicateControl",
            width: 140,
            align: "center",
            render: (value, record) => (
                <Switch
                    checked={value}
                    onChange={(val) => handleChange(record.id, "duplicateControl", val)}
                    checkedChildren="Açık"
                    unCheckedChildren="Kapalı"
                    style={{ backgroundColor: value ? "#389e0d" : "#cf1322" }}
                />
            ),
        },
        {
            title: <TitleWithTooltip title="Aktif" tooltip={tooltips.isActive} />,
            dataIndex: "isActive",
            key: "isActive",
            width: 90,
            align: "center",
            render: (value, record) => (
                <Switch
                    checked={value}
                    onChange={(val) => handleChange(record.id, "isActive", val)}
                    checkedChildren="Açık"
                    unCheckedChildren="Kapalı"
                    style={{ backgroundColor: value ? "#389e0d" : "#cf1322" }}
                />
            ),
        },
    ];

    return (
        <>
            {contextHolder}
            <Modal
                title="Ana Grup Tanımları"
                open={open}
                onCancel={onClose}
                width={1000}
                footer={[
                    <Button key="cancel" onClick={onClose}>
                        Vazgeç
                    </Button>,
                    <Button
                        key="save"
                        type="primary"
                        loading={saving}
                        onClick={handleSave}
                    >
                        Kaydet
                    </Button>,
                ]}
            >
                <Spin spinning={loading}>
                    {/* Bilgi Kutusu */}
                    <Alert
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                        message={
                            <Space>
                                <span>Kontrol ayarları hakkında bilgi almak için sütun başlıklarındaki</span>
                                <QuestionCircleOutlined style={{ color: "#1890ff" }} />
                                <span>ikonlarına tıklayın.</span>
                            </Space>
                        }
                        description={
                            <div style={{ marginTop: 8 }}>
                                <strong>Kısa Özet:</strong>
                                <ul style={{ margin: "8px 0 0 0", paddingLeft: 20 }}>
                                    <li><b>Miktar Kontrolü Kapalı:</b> Serbest giriş - aynı barkod/karekod sınırsız eklenebilir</li>
                                    <li><b>Miktar Açık + Tekrar Kapalı:</b> Onay ile tekrar eklenebilir (LENS, GÜNEŞ için önerilen)</li>
                                    <li><b>Miktar Açık + Tekrar Açık:</b> Tekrar ekleme tamamen engellenir</li>
                                </ul>
                            </div>
                        }
                    />
                    <Table
                        dataSource={data}
                        columns={columns}
                        rowKey="id"
                        pagination={false}
                        size="small"
                        bordered
                    />
                </Spin>
            </Modal>
        </>
    );
};

export default ItemTypeSettingsModal;
